import React, { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { PageHeader, PageBody, fmtMoney } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Trash, Paperclip, ArrowsClockwise } from "@phosphor-icons/react";
import { Checkbox } from "@/components/ui/checkbox";

export default function JournalsPage() {
  const { activeCompany, formatApiError } = useAuth();
  const [rows, setRows] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [open, setOpen] = useState(false);
  const [detailId, setDetailId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [reassignOpen, setReassignOpen] = useState(false);
  const [reassign, setReassign] = useState({ from_account_id: "", to_account_id: "" });
  const emptyLine = () => ({ account_id: "", debit: "", credit: "", description: "" });
  const [form, setForm] = useState({
    date: new Date().toISOString().slice(0, 10),
    memo: "",
    reference: "",
    lines: [emptyLine(), emptyLine()],
    files: [],
  });

  const load = async () => {
    if (!activeCompany) return;
    const [j, a] = await Promise.all([
      api.get(`/companies/${activeCompany.id}/journals`),
      api.get(`/companies/${activeCompany.id}/accounts`),
    ]);
    setRows(j.data);
    setAccounts(a.data);
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeCompany?.id]);

  useEffect(() => {
    if (!detailId) return;
    api.get(`/companies/${activeCompany.id}/journals/${detailId}`).then((r) => setDetail(r.data));
  }, [detailId, activeCompany?.id]);

  const setLine = (idx, k, v) => {
    const lines = [...form.lines];
    lines[idx] = { ...lines[idx], [k]: v };
    setForm({ ...form, lines });
  };

  const addLine = () => setForm({ ...form, lines: [...form.lines, emptyLine()] });
  const rmLine = (idx) =>
    setForm({ ...form, lines: form.lines.filter((_, i) => i !== idx) });

  const totalDebit = form.lines.reduce((s, l) => s + (parseFloat(l.debit) || 0), 0);
  const totalCredit = form.lines.reduce((s, l) => s + (parseFloat(l.credit) || 0), 0);

  const submit = async (e) => {
    e.preventDefault();
    try {
      const attachment_ids = [];
      for (const f of form.files) {
        const fd = new FormData();
        fd.append("file", f);
        const { data } = await api.post(`/companies/${activeCompany.id}/files`, fd);
        attachment_ids.push(data.id);
      }
      await api.post(`/companies/${activeCompany.id}/journals`, {
        date: form.date,
        memo: form.memo,
        reference: form.reference,
        lines: form.lines
          .filter((l) => l.account_id && (parseFloat(l.debit) || parseFloat(l.credit)))
          .map((l) => ({
            account_id: l.account_id,
            debit: parseFloat(l.debit) || 0,
            credit: parseFloat(l.credit) || 0,
            description: l.description,
          })),
        attachment_ids,
      });
      toast.success("Journal posted");
      setOpen(false);
      setForm({
        date: new Date().toISOString().slice(0, 10),
        memo: "",
        reference: "",
        lines: [emptyLine(), emptyLine()],
        files: [],
      });
      load();
    } catch (e2) {
      toast.error(formatApiError(e2.response?.data?.detail) || e2.message);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this journal?")) return;
    try {
      await api.delete(`/companies/${activeCompany.id}/journals/${id}`);
      toast.success("Deleted");
      load();
    } catch (e2) {
      toast.error(formatApiError(e2.response?.data?.detail) || e2.message);
    }
  };

  const toggleRow = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleAll = () => {
    setSelected((prev) =>
      prev.size === rows.length ? new Set() : new Set(rows.map((r) => r.id))
    );
  };

  const bulkDelete = async () => {
    if (selected.size === 0) return;
    if (!window.confirm(`Delete ${selected.size} journal(s)? This cannot be undone.`)) return;
    try {
      const { data } = await api.post(
        `/companies/${activeCompany.id}/journals/bulk-delete`,
        { ids: Array.from(selected) },
      );
      toast.success(`Deleted ${data.deleted} journal(s)`);
      setSelected(new Set());
      load();
    } catch (e2) {
      toast.error(formatApiError(e2.response?.data?.detail) || e2.message);
    }
  };

  const bulkReassign = async () => {
    if (!reassign.from_account_id || !reassign.to_account_id) {
      toast.error("Pick both source and target GL accounts");
      return;
    }
    if (reassign.from_account_id === reassign.to_account_id) {
      toast.error("Source and target must differ");
      return;
    }
    try {
      const { data } = await api.post(
        `/companies/${activeCompany.id}/journals/bulk-reassign-gl`,
        {
          journal_ids: Array.from(selected),
          from_account_id: reassign.from_account_id,
          to_account_id: reassign.to_account_id,
        },
      );
      toast.success(`Reclassed ${data.updated_lines} line(s) across ${data.journals_affected} journal(s)`);
      setReassignOpen(false);
      setReassign({ from_account_id: "", to_account_id: "" });
      setSelected(new Set());
      load();
    } catch (e2) {
      toast.error(formatApiError(e2.response?.data?.detail) || e2.message);
    }
  };

  if (!activeCompany) return <PageBody><div className="overline">Select a company</div></PageBody>;

  return (
    <>
      <PageHeader
        eyebrow="General Ledger"
        title="Journal Entries"
        description="Every posting is a balanced double-entry. Attach source documents to preserve audit trail."
        actions={
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button
                data-testid="new-journal-btn"
                className="rounded-sm h-9 font-mono uppercase tracking-wider text-xs"
              >
                <Plus size={14} className="mr-2" /> New Journal
              </Button>
            </DialogTrigger>
            <DialogContent className="rounded-sm max-w-3xl">
              <DialogHeader>
                <DialogTitle className="font-serif-display text-2xl">
                  New Journal Entry
                </DialogTitle>
              </DialogHeader>
              <form onSubmit={submit} className="space-y-4">
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <Label className="text-xs uppercase tracking-widest">Date</Label>
                    <Input
                      type="date"
                      data-testid="journal-date"
                      value={form.date}
                      onChange={(e) => setForm({ ...form, date: e.target.value })}
                      required
                      className="rounded-sm font-mono"
                    />
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-widest">Reference</Label>
                    <Input
                      data-testid="journal-reference"
                      value={form.reference}
                      onChange={(e) => setForm({ ...form, reference: e.target.value })}
                      className="rounded-sm font-mono"
                    />
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-widest">Memo</Label>
                    <Input
                      data-testid="journal-memo"
                      value={form.memo}
                      onChange={(e) => setForm({ ...form, memo: e.target.value })}
                      className="rounded-sm font-mono"
                    />
                  </div>
                </div>

                <div className="border border-border">
                  <div className="grid grid-cols-12 gap-2 px-3 py-2 border-b border-border bg-muted/40 overline">
                    <div className="col-span-5">Account</div>
                    <div className="col-span-2 text-right">Debit</div>
                    <div className="col-span-2 text-right">Credit</div>
                    <div className="col-span-2">Description</div>
                    <div className="col-span-1" />
                  </div>
                  {form.lines.map((l, idx) => (
                    <div key={idx} className="grid grid-cols-12 gap-2 px-3 py-2 border-b border-border last:border-b-0">
                      <div className="col-span-5">
                        <Select
                          value={l.account_id}
                          onValueChange={(v) => setLine(idx, "account_id", v)}
                        >
                          <SelectTrigger
                            data-testid={`journal-line-account-${idx}`}
                            className="rounded-sm font-mono h-9"
                          >
                            <SelectValue placeholder="Select account" />
                          </SelectTrigger>
                          <SelectContent>
                            {accounts.map((a) => (
                              <SelectItem key={a.id} value={a.id} className="font-mono">
                                {a.code} · {a.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <Input
                        type="number"
                        step="0.01"
                        value={l.debit}
                        onChange={(e) => setLine(idx, "debit", e.target.value)}
                        data-testid={`journal-line-debit-${idx}`}
                        className="col-span-2 rounded-sm font-mono text-right"
                      />
                      <Input
                        type="number"
                        step="0.01"
                        value={l.credit}
                        onChange={(e) => setLine(idx, "credit", e.target.value)}
                        data-testid={`journal-line-credit-${idx}`}
                        className="col-span-2 rounded-sm font-mono text-right"
                      />
                      <Input
                        value={l.description}
                        onChange={(e) => setLine(idx, "description", e.target.value)}
                        className="col-span-2 rounded-sm font-mono"
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => rmLine(idx)}
                        className="col-span-1 rounded-sm h-9"
                      >
                        <Trash size={14} />
                      </Button>
                    </div>
                  ))}
                  <div className="grid grid-cols-12 gap-2 px-3 py-2 bg-muted/60 border-t border-border">
                    <div className="col-span-5 flex items-center">
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        onClick={addLine}
                        className="rounded-sm font-mono text-xs h-8"
                        data-testid="journal-add-line"
                      >
                        <Plus size={12} className="mr-1" /> Add line
                      </Button>
                    </div>
                    <div className="col-span-2 text-right font-mono text-sm">
                      {totalDebit.toFixed(2)}
                    </div>
                    <div className="col-span-2 text-right font-mono text-sm">
                      {totalCredit.toFixed(2)}
                    </div>
                    <div
                      className={`col-span-3 text-right font-mono text-xs uppercase tracking-widest ${
                        totalDebit.toFixed(2) === totalCredit.toFixed(2) && totalDebit > 0
                          ? "text-primary"
                          : "text-destructive"
                      }`}
                    >
                      {totalDebit.toFixed(2) === totalCredit.toFixed(2) && totalDebit > 0
                        ? "Balanced"
                        : `Diff ${(totalDebit - totalCredit).toFixed(2)}`}
                    </div>
                  </div>
                </div>

                <div>
                  <Label className="text-xs uppercase tracking-widest">Attach invoices / receipts</Label>
                  <Input
                    type="file"
                    multiple
                    data-testid="journal-files"
                    onChange={(e) => setForm({ ...form, files: Array.from(e.target.files || []) })}
                    className="rounded-sm font-mono"
                  />
                  {form.files.length > 0 && (
                    <div className="text-xs text-muted-foreground mt-1">
                      {form.files.length} file(s) selected
                    </div>
                  )}
                </div>

                <DialogFooter>
                  <Button
                    type="submit"
                    data-testid="journal-submit"
                    className="rounded-sm font-mono uppercase tracking-wider text-xs"
                  >
                    Post Journal
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        }
      />
      <PageBody>
        {selected.size > 0 && (
          <div
            className="mb-3 p-3 border border-primary/40 bg-primary/5 rounded-sm flex items-center justify-between"
            data-testid="journal-bulk-bar"
          >
            <div className="text-sm">
              <b className="num">{selected.size}</b> journal{selected.size === 1 ? "" : "s"} selected
            </div>
            <div className="flex items-center gap-2">
              <Button
                size="sm"
                variant="outline"
                onClick={() => setReassignOpen(true)}
                data-testid="journal-bulk-reassign-btn"
              >
                <ArrowsClockwise size={14} className="mr-1" />
                Change GL
              </Button>
              <Button
                size="sm"
                variant="destructive"
                onClick={bulkDelete}
                data-testid="journal-bulk-delete-btn"
              >
                <Trash size={14} className="mr-1" />
                Delete selected
              </Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => setSelected(new Set())}
              >
                Clear
              </Button>
            </div>
          </div>
        )}
        <div className="border border-border bg-card">
          <table className="w-full text-sm">
            <thead className="border-b border-border bg-muted/40">
              <tr className="text-left">
                <Th className="w-10">
                  <Checkbox
                    checked={rows.length > 0 && selected.size === rows.length}
                    onCheckedChange={toggleAll}
                    data-testid="journal-select-all"
                  />
                </Th>
                <Th>Date</Th>
                <Th>Reference</Th>
                <Th>Memo</Th>
                <Th className="text-right">Amount</Th>
                <Th className="text-right pr-4">Actions</Th>
              </tr>
            </thead>
            <tbody>
              {rows.map((j) => (
                <tr
                  key={j.id}
                  data-testid={`journal-row-${j.id}`}
                  className="border-b border-border last:border-b-0 hover:bg-muted/40 transition-colors duration-200 cursor-pointer"
                  onClick={() => setDetailId(j.id)}
                >
                  <Td onClick={(e) => e.stopPropagation()} className="w-10">
                    <Checkbox
                      checked={selected.has(j.id)}
                      onCheckedChange={() => toggleRow(j.id)}
                      data-testid={`journal-select-${j.id}`}
                    />
                  </Td>
                  <Td className="num">{j.date}</Td>
                  <Td className="num">{j.reference || "—"}</Td>
                  <Td>
                    {j.memo || "—"}
                    {j.attachment_ids?.length > 0 && (
                      <Paperclip size={12} className="inline ml-2 text-muted-foreground" />
                    )}
                  </Td>
                  <Td className="text-right num">
                    {fmtMoney(j.total_debit, activeCompany.currency)}
                  </Td>
                  <Td className="text-right pr-4">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        remove(j.id);
                      }}
                      className="rounded-sm h-8 text-destructive hover:text-destructive"
                    >
                      <Trash size={14} />
                    </Button>
                  </Td>
                </tr>
              ))}
              {rows.length === 0 && (
                <tr>
                  <td colSpan={6} className="text-center py-8 text-muted-foreground">
                    No journal entries yet.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <Dialog open={!!detailId} onOpenChange={(v) => !v && (setDetailId(null), setDetail(null))}>
          <DialogContent className="rounded-sm max-w-2xl">
            <DialogHeader>
              <DialogTitle className="font-serif-display text-2xl">
                Journal Entry
              </DialogTitle>
            </DialogHeader>
            {detail && (
              <div className="space-y-4">
                <div className="grid grid-cols-3 gap-4 text-sm">
                  <div>
                    <div className="overline">Date</div>
                    <div className="num">{detail.date}</div>
                  </div>
                  <div>
                    <div className="overline">Reference</div>
                    <div className="num">{detail.reference || "—"}</div>
                  </div>
                  <div>
                    <div className="overline">Memo</div>
                    <div>{detail.memo || "—"}</div>
                  </div>
                </div>
                <div className="border border-border">
                  <table className="w-full text-sm">
                    <thead className="bg-muted/40 border-b border-border">
                      <tr>
                        <Th>Account</Th>
                        <Th className="text-right">Debit</Th>
                        <Th className="text-right">Credit</Th>
                      </tr>
                    </thead>
                    <tbody>
                      {detail.lines?.map((l) => {
                        const a = accounts.find((x) => x.id === l.account_id);
                        return (
                          <tr key={l.id} className="border-b border-border last:border-b-0">
                            <Td className="font-mono">{a?.code} · {a?.name}</Td>
                            <Td className="text-right num">{l.debit ? l.debit.toFixed(2) : "—"}</Td>
                            <Td className="text-right num">{l.credit ? l.credit.toFixed(2) : "—"}</Td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                {detail.attachments?.length > 0 && (
                  <div>
                    <div className="overline mb-2">Attachments</div>
                    <div className="space-y-1">
                      {detail.attachments.map((f) => (
                        <a
                          key={f.id}
                          href={`${process.env.REACT_APP_BACKEND_URL}/api/companies/${activeCompany.id}/files/${f.id}/download`}
                          target="_blank"
                          rel="noreferrer"
                          className="flex items-center gap-2 text-sm text-primary hover:underline"
                          data-testid={`attachment-${f.id}`}
                        >
                          <Paperclip size={14} />
                          {f.original_filename}
                        </a>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </DialogContent>
        </Dialog>

        <Dialog open={reassignOpen} onOpenChange={setReassignOpen}>
          <DialogContent className="rounded-sm max-w-md" data-testid="journal-reassign-dialog">
            <DialogHeader>
              <DialogTitle className="font-serif-display text-2xl">Change GL account</DialogTitle>
            </DialogHeader>
            <div className="space-y-3 text-sm">
              <div className="text-muted-foreground">
                For every selected journal, move lines posted to <b>Source GL</b> onto <b>Target GL</b>.
                Sibling lines (and the balancing side of the entry) are untouched.
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest">Source GL</Label>
                <Select
                  value={reassign.from_account_id}
                  onValueChange={(v) => setReassign({ ...reassign, from_account_id: v })}
                >
                  <SelectTrigger data-testid="journal-reassign-from" className="rounded-sm font-mono h-9">
                    <SelectValue placeholder="Pick the account to move away from" />
                  </SelectTrigger>
                  <SelectContent className="max-h-72">
                    {accounts.map((a) => (
                      <SelectItem key={a.id} value={a.id}>{a.code} · {a.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest">Target GL</Label>
                <Select
                  value={reassign.to_account_id}
                  onValueChange={(v) => setReassign({ ...reassign, to_account_id: v })}
                >
                  <SelectTrigger data-testid="journal-reassign-to" className="rounded-sm font-mono h-9">
                    <SelectValue placeholder="Pick the destination account" />
                  </SelectTrigger>
                  <SelectContent className="max-h-72">
                    {accounts.map((a) => (
                      <SelectItem key={a.id} value={a.id}>{a.code} · {a.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter>
              <Button variant="ghost" onClick={() => setReassignOpen(false)}>Cancel</Button>
              <Button onClick={bulkReassign} data-testid="journal-reassign-confirm">
                Reclass {selected.size} journal{selected.size === 1 ? "" : "s"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </PageBody>
    </>
  );
}

const Th = ({ children, className = "" }) => (
  <th className={`px-4 py-3 overline ${className}`}>{children}</th>
);
const Td = ({ children, className = "", onClick }) => (
  <td onClick={onClick} className={`px-4 py-3 ${className}`}>{children}</td>
);
